import { Type } from '@angular/core';
import { Routes } from '@angular/router';
import { landingGuard, roleGuard } from '../session/role.guard';
import { PUBLIC_SCREENS, SCREENS } from './screens';

const loadStub = () => import('./screen-stub.component').then(m => m.ScreenStubComponent);

/** Screens already built; every other P-xx keeps the stub until its own decision. */
const BUILT: Record<string, () => Promise<Type<unknown>>> = {
  'P-01': () => import('../screens/dashboard/dashboard.page').then(m => m.DashboardPage),
  'P-43': () => import('../screens/policies/policy-new.page').then(m => m.PolicyNewPage),
  'P-46': () => import('../screens/imports/import.page').then(m => m.ImportPage),
  'P-75': () => import('../screens/reports/reports.page').then(m => m.ReportsPage),
  'P-84': () => import('../screens/companies/company-list.page').then(m => m.CompanyListPage),
  'P-85': () => import('../screens/catalog/catalog.page').then(m => m.CatalogPage),
  'P-10': () => import('../screens/tickets/ticket-list.page').then(m => m.TicketListPage),
  'P-11': () => import('../screens/tickets/ticket-detail.page').then(m => m.TicketDetailPage),
  'P-20': () => import('../screens/persons/person-list.page').then(m => m.PersonListPage),
  'P-21': () => import('../screens/persons/person-detail.page').then(m => m.PersonDetailPage),
  'P-22': () => import('../screens/persons/person-form.page').then(m => m.PersonFormPage),
  'P-30': () => import('../screens/quotes/quote-list.page').then(m => m.QuoteListPage),
  'P-31': () => import('../screens/quotes/quote-new.page').then(m => m.QuoteNewPage),
  'P-32': () => import('../screens/quotes/quote-detail.page').then(m => m.QuoteDetailPage),
  'P-33': () => import('../screens/quotes/quote-share.page').then(m => m.QuoteSharePage),
  'P-34': () => import('../screens/quotes/quote-issue.page').then(m => m.QuoteIssuePage),
  'P-40': () => import('../screens/policies/policy-list.page').then(m => m.PolicyListPage),
  'P-41': () => import('../screens/policies/policy-detail.page').then(m => m.PolicyDetailPage),
  'P-44': () => import('../screens/issuance/issuance-list.page').then(m => m.IssuanceListPage),
  'P-45': () => import('../screens/renewals/renewal-list.page').then(m => m.RenewalListPage),
  'P-44d': () => import('../screens/issuance/issuance-detail.page').then(m => m.IssuanceDetailPage),
  'P-50': () => import('../screens/endorsements/endorsement-new.page').then(m => m.EndorsementNewPage),
  'P-51': () => import('../screens/endorsements/endorsement-list.page').then(m => m.EndorsementListPage),
  'P-51d': () => import('../screens/endorsements/endorsement-detail.page').then(m => m.EndorsementDetailPage),
  'P-23': () => import('../screens/compliance/legajo.page').then(m => m.LegajoPage),
  'P-70': () => import('../screens/documents/documents.page').then(m => m.DocumentsPage),
  'P-86': () => import('../screens/compliance/emission-book.page').then(m => m.EmissionBookPage),
  'P-87': () => import('../screens/compliance/unusual-reports.page').then(m => m.UnusualReportsPage),
  'P-55': () => import('../screens/collections/collections.page').then(m => m.CollectionsPage),
  'P-60': () => import('../screens/claims/claim-list.page').then(m => m.ClaimListPage),
  'P-61': () => import('../screens/claims/claim-new.page').then(m => m.ClaimNewPage),
  'P-62': () => import('../screens/claims/claim-detail.page').then(m => m.ClaimDetailPage),
  'P-65': () => import('../screens/commissions/commission-summary.page').then(m => m.CommissionSummaryPage),
  'P-66': () => import('../screens/commissions/settlement-list.page').then(m => m.SettlementListPage),
  'P-66d': () => import('../screens/commissions/settlement-detail.page').then(m => m.SettlementDetailPage),
  'P-67': () => import('../screens/commissions/agreement.page').then(m => m.AgreementPage),
  'P-68': () => import('../screens/commissions/my-commission.page').then(m => m.MyCommissionPage),
  'P-80': () => import('../screens/tree/tree.page').then(m => m.TreePage),
  'P-81': () => import('../screens/tree/member-detail.page').then(m => m.MemberDetailPage),
  'P-82': () => import('../screens/tree/invite.page').then(m => m.InvitePage),
  'P-83': () => import('../screens/tree/credentials.page').then(m => m.CredentialsPage),
  'P-35': () => import('../screens/account/quote-settings.page').then(m => m.QuoteSettingsPage),
  'P-88': () => import('../screens/audit/my-audit.page').then(m => m.MyAuditPage),
  'P-89': () => import('../screens/account/account.page').then(m => m.AccountPage),
  'P-90': () => import('../screens/admin/admin-home.page').then(m => m.AdminHomePage),
  'P-91': () => import('../screens/admin/catalog-review.page').then(m => m.CatalogReviewPage),
  'P-92': () => import('../screens/admin/connector-health.page').then(m => m.ConnectorHealthPage),
  'P-93': () => import('../screens/admin/admin-trees.page').then(m => m.AdminTreesPage),
  'P-94': () => import('../screens/audit/global-audit.page').then(m => m.GlobalAuditPage),
  'P-79': () => import('../screens/communications/communication-new.page').then(m => m.CommunicationNewPage),
  'P-78': () => import('../screens/notifications/notifications.page').then(m => m.NotificationsPage),
};

const PUBLIC_BUILT: Record<string, () => Promise<Type<unknown>>> = {
  'E-01': () => import('../screens/public/comparison.page').then(m => m.ComparisonPage),
  'E-02': () => import('../screens/public/documents.page').then(m => m.PublicDocumentsPage),
};

/** One route per P-xx screen, under the shell: the built component or the stub. */
export const PROTO_SHELL_ROUTES: Routes = [
  { path: '', pathMatch: 'full', canActivate: [landingGuard], children: [] },
  ...SCREENS.flatMap(screen =>
    screen.paths.map(path => ({
      path,
      loadComponent: BUILT[screen.pid] ?? loadStub,
      canActivate: [roleGuard],
      data: { pid: screen.pid, title: screen.title, roles: screen.roles },
    })),
  ),
];

/** Public pages for the end client (E-01, E-02): outside the shell, no guard, data under `/public/2/`. */
export const PROTO_PUBLIC_ROUTES: Routes = PUBLIC_SCREENS.map(screen => ({
  path: screen.path,
  loadComponent: PUBLIC_BUILT[screen.pid] ?? loadStub,
  data: { pid: screen.pid, title: screen.title },
}));
